import { minterRoleId } from '@components/SpaceSettingsPanel/rolePermissions.const'
import { readableDuration } from '@components/Web3/Subscriptions/SubscriptionsPanel'
import { getChannelEntitlementsQueryData } from 'hooks/useChannelEntitlements'
import { channelIdFromPathname } from 'hooks/useChannelIdFromPathname'
import { getEntitlementsQueryData } from 'hooks/useEntitlements'
import { spaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { getMembershipInfoQueryData, getPricingModuleQueryData } from 'use-towns-client'

import { getSpaceNameFromCache } from './getSpaceNameFromCache'

export type TokenGatedByAnalytics = {
    address: string
    chainId: number
    tokenType: string
    quantity: string | undefined
    tokenId: string | undefined
}

export type SpaceDetailsAnalytics = {
    spaceId: string | undefined
    spaceName: string | undefined
    gatedSpace: boolean | undefined
    pricingModule: 'fixed' | 'dynamic' | 'free' | undefined
    priceInWei: string | undefined
    tokensGatedBy: TokenGatedByAnalytics[] | undefined
}

type TrackedDetails = {
    gatedSpace: boolean | undefined
    pricingModule: 'free' | 'fixed' | 'dynamic' | undefined
    priceInWei: string | undefined
    tokensGatedBy: TokenGatedByAnalytics[] | undefined
    gatedChannel?: boolean | undefined
    channelName?: string | undefined
    spaceName: string | undefined
    duration: string | undefined
}

// this will grab cached data from react query
// if the query hasn't run yet, some data will be undefined
// this is intentional, we're not gonna make extra requests and/or slow down the app to send analytics events
export const getCachedSpaceDataForAnalytics = (): TrackedDetails => {
    try {
        const _spaceId = spaceIdFromPathname(location.pathname)
        const _channelId = channelIdFromPathname(location.pathname)

        const spaceId = _spaceId ?? ''
        const channelId = _channelId ?? ''

        const pricingData = getPricingModuleQueryData({
            spaceId: spaceId ?? _spaceId,
        })
        const channelEntitlements = getChannelEntitlementsQueryData({
            spaceId,
            channelId,
        })
        const entitlements = getEntitlementsQueryData(spaceId, minterRoleId)
        const membershipInfo = getMembershipInfoQueryData(spaceId)

        const isFixed = pricingData?.data?.isFixed
        const priceInWei = pricingData?.data?.price.toString()
        const isFree = isFixed && priceInWei === '0'
        const duration = readableDuration(membershipInfo?.duration ?? 0)

        const track: TrackedDetails = {
            gatedSpace: entitlements?.hasEntitlements,
            pricingModule: pricingData?.data
                ? isFree
                    ? 'free'
                    : isFixed
                      ? 'fixed'
                      : 'dynamic'
                : undefined,
            priceInWei: priceInWei,
            spaceName: getSpaceNameFromCache(spaceId),
            duration: duration,
            tokensGatedBy:
                entitlements?.tokens?.map((t) => ({
                    address: t?.data?.address ?? '',
                    chainId: t?.chainId ?? 0,
                    tokenType: t?.data?.type ?? '',
                    quantity: t?.data?.quantity ?? '',
                    tokenId: t?.data?.tokenId,
                })) ?? undefined,
        } as const

        if (channelEntitlements) {
            track.gatedChannel = channelEntitlements.hasSomeEntitlement
            track.channelName = channelEntitlements.channelName
        }

        return track
    } catch (error) {
        console.log('[getAllDetails] error', error)
        return {
            gatedSpace: undefined,
            pricingModule: undefined,
            priceInWei: undefined,
            tokensGatedBy: undefined,
            spaceName: undefined,
            duration: undefined,
        }
    }
}
